import FilterCheckbox from "@/components/FilterCheckbox";
import FilterSelect from "@/components/FilterSelect";
import SearchFocusButton from "@/components/SearchFocusButton";
import { SORT, SORT_ITEM_MAP, SORT_LIST } from "@/constants/sort";
import splitTopicUrlnameAndFilterId from "@/utils/splitTopicUrlnameAndFilterId";
import { Form } from "antd";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Else, If, Then } from "react-if";
import styles from "./index.module.less";
import TopicNav from "./nav";

function FilterCheckboxWithData({ data, value, onChange, ...props }: any) {
  const onValueChange = useCallback(
    (checked) => {
      onChange && onChange(checked ? data : null);
    },
    [onChange, data]
  );

  return <FilterCheckbox value={!!value} onChange={onValueChange} {...props} />;
}

export default function TopicFilter({ topic, onChange }) {
  const [values, setValues] = useState(null);
  const formRef = useRef(null);
  const router = useRouter();

  const { urlname: params } = router.query;
  const { urlname, filter_id: filterId } = splitTopicUrlnameAndFilterId(params);

  useEffect(() => {
    const values = {
      sort: SORT_ITEM_MAP[SORT.ALL],
    };
    formRef.current.setFieldsValue(values);
    setValues(values);
  }, []);

  const groupOptions = useMemo(() => {
    return (
      topic?.contents?.map((content) => {
        return {
          label: content.title,
          img: content.img,
          value: content.filter.value,
        };
      }) || []
    );
  }, [topic?.contents]);

  const groupValue = useMemo(() => {
    if (!groupOptions.length) {
      return null;
    }

    if (filterId) {
      return (
        groupOptions.find((option) => `${option.value}` === `${filterId}`) ||
        groupOptions[0]
      );
    }

    return groupOptions[0];
  }, [filterId, groupOptions]);

  const onGroupChange = useCallback(
    (value) => {
      router.push(`/topics/${urlname}-${value.value}`);
    },
    [router, urlname]
  );

  const onValuesChange = useCallback((_, values) => {
    setValues(values);
  }, []);

  useEffect(() => {
    if (values && groupValue && values.sort) {
      let ids = [groupValue.value];
      Object.values(values?.filter || {}).forEach((item: any) => {
        if (item) {
          ids.push(item.value);
        }
      });
      onChange &&
        onChange({
          ids,
          sort: values.sort.value,
          // 这个用于埋点
          sortName: values.sort.label,
          filterOne: groupValue.label,
        });
    }
  }, [onChange, values, groupValue]);

  return (
    <Form
      className={styles.container}
      layout="inline"
      ref={formRef}
      onValuesChange={onValuesChange}
    >
      {/* 如果只有1个子主题，就不展示子场景的名字 */}
      {groupOptions?.length > 1 && (
        <div className={styles.nav}>
          <TopicNav
            options={groupOptions}
            value={groupValue}
            onChange={onGroupChange}
          />
        </div>
      )}

      <div className={styles.filter}>
        <div className={styles.left}>
          <Form.Item name="sort">
            <FilterSelect allowClear={false} options={SORT_LIST} />
          </Form.Item>
        </div>
        <div className={styles.right}>
          {topic?.filters
            ?.filter((item) => item.showOutside)
            ?.map((filter) => (
              <If key={filter.key} condition={filter.children.length <= 0}>
                <Then>
                  <Form.Item name={["filter", `${filter.key}`]}>
                    <FilterCheckboxWithData
                      className={styles.item}
                      name={filter.name}
                      data={filter}
                      iconType="img"
                      icon={filter.icon}
                    />
                  </Form.Item>
                </Then>
                <Else>
                  <Form.Item name={["filter", `${filter.key}`]}>
                    <FilterSelect
                      className={styles.item}
                      iconType="img"
                      optionType={filter.type}
                      icon={filter.icon}
                      name={filter.name}
                      options={filter.children}
                    />
                  </Form.Item>
                </Else>
              </If>
            ))}
          <SearchFocusButton
            className={styles.button}
            searchScope="searchTopicPin"
          />
        </div>
      </div>
    </Form>
  );
}
